/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/components/SmsSendModal.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    Modal for sending new SMS messages - 4-column inline layout
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Select, { SelectOption } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { useNotifications } from '../../../contexts/NotificationContext';
import { SendSmsFormData, SmsNumber } from '../types';
import { sendMessage, fetchNumbers } from '../api';

interface SmsSendModalProps {
    isOpen: boolean;
    onClose: (refresh?: boolean) => void;
}

// GSM-7 character set for encoding detection
const GSM7_CHARS = new Set([
    '@', '£', '$', '¥', 'è', 'é', 'ù', 'ì', 'ò', 'Ç', '\n', 'Ø', 'ø', '\r',
    'Å', 'å', 'Δ', '_', 'Φ', 'Γ', 'Λ', 'Ω', 'Π', 'Ψ', 'Σ', 'Θ', 'Ξ',
    'Æ', 'æ', 'ß', 'É', ' ', '!', '"', '#', '¤', '%', '&', "'", '(', ')',
    '*', '+', ',', '-', '.', '/', '0', '1', '2', '3', '4', '5', '6', '7',
    '8', '9', ':', ';', '<', '=', '>', '?', '¡', 'A', 'B', 'C', 'D', 'E',
    'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S',
    'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'Ä', 'Ö', 'Ñ', 'Ü', '§', '¿', 'a',
    'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o',
    'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'ä', 'ö', 'ñ',
    'ü', 'à', '|', '^', '€', '{', '}', '[', ']', '~', '\\'
]);

function analyzeMessage(body: string) {
    let isGSM7 = true;
    for (const char of body) {
        if (!GSM7_CHARS.has(char)) {
            isGSM7 = false;
            break;
        }
    }

    const encoding = isGSM7 ? 'GSM7' : 'UCS2';
    const length = body.length;

    let segments = 1;
    if (encoding === 'GSM7') {
        segments = length <= 160 ? 1 : Math.ceil(length / 153);
    } else {
        segments = length <= 70 ? 1 : Math.ceil(length / 67);
    }

    const maxLength = encoding === 'GSM7'
        ? (segments === 1 ? 160 : segments * 153)
        : (segments === 1 ? 70 : segments * 67);
    const remaining = maxLength - length;

    return { encoding, segments, length, remaining };
}

export default function SmsSendModal({ isOpen, onClose }: SmsSendModalProps) {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const [sending, setSending] = useState(false);
    const [numbers, setNumbers] = useState<SmsNumber[]>([]);
    const [formData, setFormData] = useState<SendSmsFormData>({
        from: '',
        to: '',
        body: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const analysis = useMemo(() => analyzeMessage(formData.body || ''), [formData.body]);

    useEffect(() => {
        if (isOpen) {
            loadNumbers();
            setFormData({ from: '', to: '', body: '' });
            setErrors({});
        }
    }, [isOpen]);

    const loadNumbers = async () => {
        try {
            const data = await fetchNumbers();
            setNumbers(data.filter(n => n.enabled));
        } catch (error) {
            console.error('Failed to load numbers:', error);
        }
    };

    const numberOptions = useMemo((): SelectOption[] => {
        return numbers.map(n => ({
            value: n.phoneNumber,
            label: n.friendlyName ? `${n.phoneNumber} (${n.friendlyName})` : n.phoneNumber
        }));
    }, [numbers]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleFieldChange = useCallback((field: keyof SendSmsFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
    }, [errors]);

    const validateForm = useCallback(() => {
        const newErrors: Record<string, string> = {};

        if (!formData.from) {
            newErrors.from = t('validation.required');
        }

        if (!formData.to) {
            newErrors.to = t('validation.required');
        } else if (!/^\+[1-9]\d{1,14}$/.test(formData.to)) {
            newErrors.to = t('sms.messages.invalidE164');
        }

        if (!formData.body?.trim()) {
            newErrors.body = t('validation.required');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, t]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setSending(true);
            const result = await sendMessage(formData);
            showSuccess(t('sms.messages.messages.sendSuccess', {
                messageId: result.messageId.substring(0, 8),
                segments: result.segments
            }));
            onClose(true);
        } catch (error) {
            showError(error.message || t('sms.messages.messages.sendError'));
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => onClose()}
            title={t('sms.messages.sendTitle')}
            size="lg"
        >
            <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    {/* Row 1: From | Select | To | Input */}
                    <SimpleTooltip content={t('sms.messages.fields.from')} position="top">
                        <Label htmlFor="from" required className="text-left">
                            {t('sms.messages.fields.from')}
                        </Label>
                    </SimpleTooltip>
                    <Select
                        value={formData.from}
                        onValueChange={(value) => handleFieldChange('from', value)}
                        options={numberOptions}
                        placeholder={t('sms.messages.selectFrom')}
                        searchable={true}
                    />
                    <SimpleTooltip content={t('sms.messages.fields.to')} position="top">
                        <Label htmlFor="to" required className="text-left">
                            {t('sms.messages.fields.to')}
                        </Label>
                    </SimpleTooltip>
                    <Input
                        id="to"
                        value={formData.to}
                        onChange={(e) => handleFieldChange('to', e.target.value)}
                        placeholder="+15551234567"
                        error={errors.to}
                    />
                </div>

                {/* Message body - full width */}
                <div className="space-y-2">
                    <SimpleTooltip content={t('sms.messages.fields.body')} position="top">
                        <Label htmlFor="body" required>
                            {t('sms.messages.fields.body')}
                        </Label>
                    </SimpleTooltip>
                    <Textarea
                        id="body"
                        value={formData.body}
                        onChange={(e) => handleFieldChange('body', e.target.value)}
                        placeholder={t('sms.messages.bodyPlaceholder')}
                        rows={5}
                        maxLength={1600}
                    />
                    {errors.body && <span className="text-sm text-red-500">{errors.body}</span>}

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                            <Badge variant={analysis.encoding === 'GSM7' ? 'outline' : 'secondary'}>
                                {analysis.encoding}
                            </Badge>
                            <span className="text-muted-foreground">
                                {analysis.length} {t('sms.messages.chars')} •
                                {analysis.segments} {analysis.segments === 1 ? t('sms.messages.segment') : t('sms.messages.segments')}
                            </span>
                        </div>
                        <span className={`${analysis.remaining < 20 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                            {analysis.remaining} {t('sms.messages.remaining')}
                        </span>
                    </div>
                </div>

                <div className="flex gap-2 justify-end pt-4 border-t border-border">
                    <Button type="button" variant="outline" onClick={() => onClose()}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={sending}>
                        {sending ? t('sms.messages.sending') : t('sms.messages.send')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
